'use client';

import { Bars2Icon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { AnimatePresence, m } from 'motion/react';
import Link from 'next/link';
import { useEffect, useId, useRef, useState } from 'react';

import { useModal } from '@/app/pageContext';

import { Button, Logo } from './primitives';

const PRODUCT_LINKS = [
  { href: '/#process', label: 'How it works' },
  { href: '/#scope', label: 'Coverage' },
  { href: '/#faq', label: 'Questions' },
  { href: '/about/', label: 'About' },
];

/** Disclosure navigation menu: a button that reveals a list of links. */
function StatesMenu({
  stateLinks,
  currentSlug,
}: {
  stateLinks: { href: string; label: string }[];
  currentSlug: string;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        root.current?.querySelector('button')?.focus();
      }
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((v) => !v)}
        className="flex cursor-pointer items-center gap-1 rounded-full text-[0.95rem] font-medium text-ink-900/80 transition-colors hover:text-ink-900 aria-expanded:text-ink-900"
      >
        States
        <ChevronDownIcon
          className={clsx('h-4 w-4 transition-transform duration-200', open && 'rotate-180')}
          aria-hidden="true"
        />
      </button>
      {open ? (
        <ul
          id={id}
          className="disclosure-panel absolute top-full right-0 z-50 mt-4 w-60 rounded-2xl bg-white p-2 text-ink-900 shadow-[0_24px_60px_-24px_rgba(11,18,32,0.5)]"
        >
          {stateLinks.map((s) => (
            <li key={s.href}>
              <Link
                href={s.href}
                aria-current={`/${currentSlug}/` === s.href ? 'page' : undefined}
                onClick={() => setOpen(false)}
                className="block rounded-xl px-3 py-2 text-[0.95rem] hover:bg-frost-100 focus-visible:bg-frost-100 aria-[current=page]:font-semibold"
              >
                {s.label}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function Nav({
  stateLinks,
  currentSlug,
  cta,
}: {
  stateLinks: { href: string; label: string }[];
  currentSlug: string;
  cta: string;
}) {
  const { openModal } = useModal();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setScrolled(window.scrollY > 24));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <header className="fixed inset-x-0 top-0 z-40 px-4 pt-4 sm:px-7 sm:pt-7">
      <nav
        aria-label="Primary"
        className={clsx(
          'mx-auto flex h-14 w-full max-w-[1320px] items-center justify-between rounded-full bg-white/[0.96] pr-2 pl-4 text-ink-900 backdrop-blur-md transition-shadow duration-300 sm:h-16 sm:pl-5',
          scrolled
            ? 'shadow-[0_18px_40px_-20px_rgba(11,18,32,0.45)]'
            : 'shadow-[0_10px_30px_-18px_rgba(11,18,32,0.35)]',
        )}
      >
        <Link href="/" className="rounded-full" aria-label="RoosterBeacon, home">
          <Logo />
        </Link>

        <div className="hidden items-center gap-7 lg:flex">
          {PRODUCT_LINKS.map((l) => (
            <Link
              key={l.href}
              href={l.href}
              className="rounded-full text-[0.95rem] font-medium text-ink-900/80 transition-colors hover:text-ink-900"
            >
              {l.label}
            </Link>
          ))}
          <StatesMenu stateLinks={stateLinks} currentSlug={currentSlug} />
        </div>

        <div className="flex items-center gap-1.5">
          <div className="hidden lg:block">
            <Button variant="beacon" className="min-h-11 px-5" onClick={openModal}>
              {cta}
            </Button>
          </div>
          <button
            type="button"
            className="inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-frost-100 lg:hidden"
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? 'Close menu' : 'Open menu'}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? (
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            ) : (
              <Bars2Icon className="h-6 w-6" aria-hidden="true" />
            )}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open ? (
          <m.div
            id="mobile-menu"
            key="menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            className="on-ink fixed inset-x-4 top-[4.75rem] bottom-4 z-40 overflow-y-auto rounded-[1.75rem] bg-ink-950 text-white lg:hidden"
          >
            <div className="flex min-h-full flex-col justify-between p-6">
              <div>
                <ul className="divide-y divide-white/10 border-b border-white/10">
                  {PRODUCT_LINKS.map((l) => (
                    <li key={l.href}>
                      <Link
                        href={l.href}
                        onClick={() => setOpen(false)}
                        className="font-display block py-4 text-[1.75rem] leading-none font-bold tracking-[-0.02em]"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
                <p className="mt-7 mb-3 text-sm font-semibold text-beacon-400">Payer guides by state</p>
                <ul className="grid grid-cols-2 gap-x-6">
                  {stateLinks.map((s) => (
                    <li key={s.href}>
                      <Link
                        href={s.href}
                        onClick={() => setOpen(false)}
                        className="block py-2 text-base text-white/90"
                      >
                        {s.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <Button
                variant="beacon"
                className="mt-10 w-full"
                onClick={() => {
                  setOpen(false);
                  openModal();
                }}
              >
                {cta}
              </Button>
            </div>
          </m.div>
        ) : null}
      </AnimatePresence>
    </header>
  );
}

/** Phone-only action bar that appears once the hero has scrolled away. */
export function MobileActionBar({ cta }: { cta: string }) {
  const { openModal } = useModal();
  const [show, setShow] = useState(false);

  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const past = window.scrollY > window.innerHeight * 0.75;
        const cta = document.getElementById('early-access');
        const nearEnd = cta ? cta.getBoundingClientRect().top < window.innerHeight * 0.6 : false;
        setShow(past && !nearEnd);
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <AnimatePresence>
      {show ? (
        <m.div
          key="bar"
          initial={{ y: 96 }}
          animate={{ y: 0 }}
          exit={{ y: 96 }}
          transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-x-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-30 lg:hidden"
        >
          <Button
            variant="beacon"
            className="w-full shadow-[0_18px_40px_-16px_rgba(59,102,245,0.7)]"
            onClick={openModal}
          >
            {cta}
          </Button>
        </m.div>
      ) : null}
    </AnimatePresence>
  );
}
